import React, { useEffect, useState } from 'react';
import { useVerifyToken } from '../../hooks/useVerifyToken';
import { usePlanSelection } from '../../contexts/PlanSelectionContext';
import Modal from '../../../../components/common/Modal';
import emailVerifiedIcon from '../../../../assets/Images/email-verified.png';
import { getUtmQsParams } from '@/shared/utils/utmUtils';
import './EmailVerifiedSection.css';

interface EmailVerifiedSectionProps {
  onContinue?: () => void;
}

const EmailVerifiedSection: React.FC<EmailVerifiedSectionProps> = () => {
  const [countdown, setCountdown] = useState(3);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [showInvalidLinkModal, setShowInvalidLinkModal] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  
  // Get URL parameters for the new route /email/verified/plan-details
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get('id');
  const token = urlParams.get('token');
  const isNewEmailVerificationRoute = window.location.pathname.includes('/email/verified/plan-details');
  
  const utmQsParams = getUtmQsParams(urlParams);

  // Use verification hook only for new route
  const { verifyToken, loading: tokenLoading } = useVerifyToken();
  
  // Access PlanSelectionContext to store and retrieve tempData
  const { setTempData, tempData } = usePlanSelection();

  // Perform verification on mount for new route only
  useEffect(() => {
    const performVerification = async () => {
      // Only perform verification for the new email verification route
      if (!isNewEmailVerificationRoute || !token || !id) {
        // For the original /email/verified route, just start countdown
        if (!isNewEmailVerificationRoute) {
          setVerificationComplete(true);
        }
        return;
      }

      try {
        console.log('Starting email verification process for new route...', { id, token: token.substring(0, 8) + '...' });

        // CRITICAL FIX: Verify token now returns tempData directly from backend
        // No need for separate retrieveTempData API call
        const tokenResult = await verifyToken(token);
        if (!tokenResult.success) {
          throw new Error(`Token verification failed: ${tokenResult.message}`);
        }

        console.log('Token verification successful with tempData:', tokenResult.tempData);

        // Validate that tempData was returned
        if (!tokenResult.tempData) {
          throw new Error('Token verification succeeded but no tempData was returned');
        }

        // Store tempData in PlanSelectionContext immediately
        console.log('EmailVerifiedSection: Storing tempData in PlanSelectionContext:', tokenResult.tempData);
        setTempData(tokenResult.tempData);
        
        // Optional: Extract principal data if available in tempData for consistency
        if (tokenResult.tempData && typeof tokenResult.tempData === 'object') {
          const tempDataObj = tokenResult.tempData as Record<string, unknown>;
          if (tempDataObj.principalId || tempDataObj.principal_id) {
            console.log('EmailVerifiedSection: Extracting principal data from tempData for consistency');
            // Note: This would require importing setPrincipalData from usePlanSelection
            // For now, we'll leave this as a comment since EmailVerifiedSection should remain untouched
            // setPrincipalData({
            //   principalId: tempDataObj.principalId as number,
            //   principal_id: tempDataObj.principal_id as number,
            //   email: tempDataObj.email as string
            // });
          }
        }
        const verificationTempData = {"email":tokenResult.tempData.email, "isVerified":true, "timestamp":new Date().getTime()};
        localStorage.setItem('emailVerificationTempData', JSON.stringify(verificationTempData));
        sessionStorage.setItem('emailVerificationTempData', JSON.stringify(verificationTempData));
        setVerificationComplete(true);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown verification error';
        console.error('Verification process failed:', errorMessage);
        setVerificationError(errorMessage);
        
        // Show invalid link modal for verification failures
        setShowInvalidLinkModal(true);
        
        // Start countdown for redirect to landing page
        const redirectTimer = setInterval(() => {
          setRedirectCountdown(prev => {
            if (prev <= 1) {
              clearInterval(redirectTimer);
              // Redirect to landing page
              window.location.href = '/travel-portal/customer-portal' + utmQsParams;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    };

    performVerification();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  // Start countdown after verification is complete
  useEffect(() => {
    if (verificationComplete && !isRedirecting && !verificationError) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [verificationComplete, isRedirecting, verificationError]);

  const handleRedirect = () => {
    if (isRedirecting) return;
    setIsRedirecting(true);

    // FIXED: Separate routing logic for isVerified===true vs isVerified===false cases
    // For new email verification route, we get tempData from the verify token response
    if (isNewEmailVerificationRoute) {
      // Get tempData from context (already stored during verification)
      const contextTempData = tempData as Record<string, unknown> | null;
      
      if (!contextTempData) {
        console.error('EmailVerifiedSection: No tempData available for redirect');
        // Fallback to landing page
        window.location.href = '/travel-portal/customer-portal' + utmQsParams;
        return;
      }
      // FOR isVerified===false: Use complex tempData detection logic (NEW EMAIL VERIFICATION ROUTE)
      console.log('🔄 EmailVerifiedSection: Processing isVerified===false case (new email verification route)');
      
      // COMPREHENSIVE PLAN TYPE DETECTION - Multiple fallback strategies with extensive debugging
      let typeName = 'Individual'; // Final fallback
      
      console.log('🔍 EmailVerifiedSection: DETAILED tempData analysis:', {
        fullTempData: contextTempData,
        keys: Object.keys(contextTempData),
        typeName: contextTempData.typeName,
        planType: contextTempData.planType,
        productName: contextTempData.productName,
        // Check for other possible plan type fields
        type: contextTempData.type,
        planTypeName: contextTempData.planTypeName,
        category: contextTempData.category,
        plan: contextTempData.plan
      });
      
      // Strategy 1: Use typeName directly if available and valid
      if (contextTempData.typeName && typeof contextTempData.typeName === 'string') {
        const cleanTypeName = contextTempData.typeName.trim();
        if (cleanTypeName && cleanTypeName !== '') {
          typeName = cleanTypeName;
          console.log('✅ EmailVerifiedSection: Using typeName from tempData:', typeName);
        }
      }
      
      // Strategy 2: Convert planType number to string if typeName is not available or invalid
      else if (contextTempData.planType !== undefined && contextTempData.planType !== null) {
        const planTypeValue = contextTempData.planType;
        let planTypeNumber: number;
        
        console.log('🔄 EmailVerifiedSection: Converting planType to typeName:', {
          planTypeValue,
          type: typeof planTypeValue,
          isNaN: isNaN(Number(planTypeValue))
        });
        
        if (typeof planTypeValue === 'number') {
          planTypeNumber = planTypeValue;
        } else if (typeof planTypeValue === 'string') {
          planTypeNumber = parseInt(planTypeValue, 10);
          if (isNaN(planTypeNumber)) {
            console.warn('⚠️ EmailVerifiedSection: planType string is not a valid number:', planTypeValue);
            planTypeNumber = 0; // Invalid
          }
        } else {
          console.warn('⚠️ EmailVerifiedSection: Invalid planType format:', planTypeValue);
          planTypeNumber = 0; // Invalid
        }
        
        // Map planType number to string
        if (planTypeNumber > 0 && planTypeNumber <= 4) {
          switch (planTypeNumber) {
            case 1:
              typeName = 'Individual';
              break;
            case 2:
              typeName = 'Family';
              break;
            case 3:
              typeName = 'Group';
              break;
            case 4:
              typeName = 'Multi Trip';
              break;
          }
          console.log('✅ EmailVerifiedSection: Converted planType number to typeName:', {
            planTypeNumber,
            typeName
          });
        } else {
          console.warn('⚠️ EmailVerifiedSection: Invalid planType number:', planTypeNumber, 'trying other strategies');
        }
      }
      
      // Strategy 3: Try to detect from productName if available
      if (typeName === 'Individual' && contextTempData.productName && typeof contextTempData.productName === 'string') {
        const productName = contextTempData.productName.toLowerCase();
        console.log('🔍 EmailVerifiedSection: Analyzing productName:', productName);
        
        if (productName.includes('multi') || productName.includes('annual')) {
          typeName = 'Multi Trip';
          console.log('✅ EmailVerifiedSection: Detected Multi Trip from productName:', contextTempData.productName);
        } else if (productName.includes('family')) {
          typeName = 'Family';
          console.log('✅ EmailVerifiedSection: Detected Family from productName:', contextTempData.productName);
        } else if (productName.includes('group')) {
          typeName = 'Group';
          console.log('✅ EmailVerifiedSection: Detected Group from productName:', contextTempData.productName);
        }
      }
      
      // Strategy 4: Check other possible fields in tempData
      if (typeName === 'Individual') {
        const possibleFields = ['type', 'planTypeName', 'category', 'plan'];
        for (const field of possibleFields) {
          if (contextTempData[field] && typeof contextTempData[field] === 'string') {
            const fieldValue = (contextTempData[field] as string).toLowerCase();
            console.log(`🔍 EmailVerifiedSection: Checking field '${field}':`, fieldValue);
            
            if (fieldValue.includes('multi') || fieldValue.includes('annual')) {
              typeName = 'Multi Trip';
              console.log(`✅ EmailVerifiedSection: Detected Multi Trip from ${field}:`, contextTempData[field]);
              break;
            } else if (fieldValue.includes('family')) {
              typeName = 'Family';
              console.log(`✅ EmailVerifiedSection: Detected Family from ${field}:`, contextTempData[field]);
              break;
            } else if (fieldValue.includes('group')) {
              typeName = 'Group';
              console.log(`✅ EmailVerifiedSection: Detected Group from ${field}:`, contextTempData[field]);
              break;
            }
          }
        }
      }
      
      console.log('🎯 EmailVerifiedSection: Final plan type resolution for isVerified===false:', {
        originalTempData: {
          typeName: contextTempData.typeName,
          planType: contextTempData.planType,
          productName: contextTempData.productName,
          allFields: Object.keys(contextTempData).reduce((acc, key) => {
            acc[key] = contextTempData[key];
            return acc;
          }, {} as Record<string, unknown>)
        },
        finalTypeName: typeName,
        detectionStrategy: contextTempData.typeName ? 'typeName' :
                          contextTempData.planType ? 'planType' :
                          contextTempData.productName ? 'productName' : 'fieldScan',
        wasDefaultUsed: typeName === 'Individual'
      });
      
      // Convert typeName to URL slug (same logic as existing system)
      const convertPlanTypeToSlug = (planType: string): string => {
        const typeMap: Record<string, string> = {
          'Individual Plan': 'individual',
          'Individual': 'individual',
          'Family Plan': 'family',
          'Family': 'family',
          'Group Plan': 'group',
          'Group': 'group',
          'Multi Trip': 'multi-trip'
        };
        return typeMap[planType] || 'individual';
      };

      const planSlug = convertPlanTypeToSlug(typeName);
      const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || '';
      const redirectUrl = `${APP_BASE_URL}customer-portal/client-information/${planSlug}` + utmQsParams;

      // Enhanced tempData storage with additional metadata for better session handling
      const enhancedTempData = {
        ...contextTempData,
        // Add metadata for better tracking
        emailVerificationTimestamp: new Date().toISOString(),
        resolvedPlanType: typeName,
        planSlug: planSlug,
        // Ensure critical data is preserved
        rateAmount: contextTempData.rateAmount || 0,
        currencyCode: contextTempData.currencyCode || 'PHP',
        productName: contextTempData.productName || 'Unknown Product',
        isVerified: true
      };

      // CRITICAL FIX: Store in localStorage (persists across browser sessions)
      // This solves the issue where closing the window loses sessionStorage data
      localStorage.setItem('emailVerificationComplete', 'true');
      localStorage.setItem('emailVerificationTempData', JSON.stringify(enhancedTempData));
      localStorage.setItem('emailVerificationPlanType', typeName);
      localStorage.setItem('emailVerificationShouldSetupClient', 'true');
      localStorage.setItem('emailVerificationTimestamp', new Date().toISOString());
      
      // Store verification ID to detect new verification sessions
      const urlParams = new URLSearchParams(window.location.search);
      const verificationId = urlParams.get('id');
      if (verificationId) {
        localStorage.setItem('currentVerificationId', verificationId);
      }
      
      // Also store in sessionStorage for same-session navigation
      sessionStorage.setItem('emailVerificationComplete', 'true');
      sessionStorage.setItem('emailVerificationTempData', JSON.stringify(enhancedTempData));
      sessionStorage.setItem('emailVerificationPlanType', typeName);
      sessionStorage.setItem('emailVerificationShouldSetupClient', 'true');
      
      console.log('EmailVerifiedSection: Redirecting with enhanced tempData (isVerified===false flow):', {
        redirectUrl,
        originalTempData: contextTempData,
        enhancedTempData,
        planTypeMapping: {
          originalPlanType: contextTempData.planType,
          originalTypeName: contextTempData.typeName,
          resolvedTypeName: typeName,
          planSlug
        }
      });
      
      // CRITICAL FIX: Store tempData in PlanSelectionContext before redirect as well
      console.log('EmailVerifiedSection: Storing enhanced tempData in PlanSelectionContext before redirect:', enhancedTempData);
      setTempData(enhancedTempData);
      
      // Small delay to ensure context is updated before redirect
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 100);
    } else {
      // FOR isVerified===true: Use simple routing based on current URL path (ORIGINAL EMAIL VERIFIED ROUTE)
      console.log('🔄 EmailVerifiedSection: Processing isVerified===true case (original email verified route)');
      
      // CRITICAL FIX: For isVerified===true cases, DO NOT set sessionStorage data
      // This prevents CustomerPortalSection from detecting it as email verification flow
      console.log('🚫 EmailVerifiedSection: NOT setting sessionStorage for isVerified===true case to prevent flow contamination');
      
      // CLEANUP: Remove any existing email verification sessionStorage data to prevent contamination
      sessionStorage.removeItem('emailVerificationComplete');
      sessionStorage.removeItem('emailVerificationTempData');
      sessionStorage.removeItem('emailVerificationPlanType');
      sessionStorage.removeItem('emailVerificationShouldSetupClient');
      localStorage.removeItem('emailVerificationTempDataBackup');
      console.log('🧹 EmailVerifiedSection: Cleaned up any existing email verification sessionStorage data');
      
      // Extract plan type from current URL path - this preserves the plan type selected in travel flow
      const currentPath = window.location.pathname;
      console.log('🔍 EmailVerifiedSection: Analyzing current path for plan type:', currentPath);
      
      // Extract plan type from URL path like customer-portal/client-information/family
      const pathSegments = currentPath.split('/').filter(segment => segment);
      let detectedPlanSlug = 'individual'; // Default fallback
      
      // Look for client-information segment and get the next segment as plan type
      const clientInfoIndex = pathSegments.indexOf('client-information');
      if (clientInfoIndex !== -1 && clientInfoIndex + 1 < pathSegments.length) {
        const planSlugFromPath = pathSegments[clientInfoIndex + 1];
        if (['individual', 'family', 'group', 'multi-trip'].includes(planSlugFromPath)) {
          detectedPlanSlug = planSlugFromPath;
          console.log('✅ EmailVerifiedSection: Detected plan type from URL path:', planSlugFromPath);
        }
      }
      
      // Convert slug back to plan type name for consistency
      const slugToPlanTypeMap: Record<string, string> = {
        'individual': 'Individual',
        'family': 'Family',
        'group': 'Group',
        'multi-trip': 'Multi Trip'
      };
      
      const detectedPlanType = slugToPlanTypeMap[detectedPlanSlug] || 'Individual';
      
      console.log('🎯 EmailVerifiedSection: Final plan type resolution for isVerified===true:', {
        currentPath,
        detectedPlanSlug,
        detectedPlanType,
        source: 'URL_PATH_EXTRACTION',
        noSessionStorageSet: true
      });
      
      const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || '';
      const redirectUrl = `${APP_BASE_URL}customer-portal/client-information/${detectedPlanSlug}` + utmQsParams;
      
      console.log('EmailVerifiedSection: Redirecting for isVerified===true case:', {
        redirectUrl,
        planType: detectedPlanType,
        planSlug: detectedPlanSlug,
        preservedFromTravelFlow: true,
        noSessionStorageContamination: true
      });
      
      // For isVerified===true, we don't need to store complex tempData since the user already went through the flow
      // Just redirect to the appropriate plan type page WITHOUT setting sessionStorage
      window.location.href = redirectUrl;
    }
  };

  // Loading state for new route
  if (isNewEmailVerificationRoute && tokenLoading && !verificationComplete) {
    return (
      <div className="email-verified-container">
        <div className="email-verified-content">
          <div className="email-verified-icon-container">
            <img 
              src={emailVerifiedIcon} 
              alt="Email Verified" 
              className="email-verified-icon" 
            />
          </div>
          <h1 className="email-verified-title">Verifying</h1>
          <p className="email-verified-message">
            Please wait while we verify your email address...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (verificationError) {
    return (
      <div className="email-verified-container">
        <div className="email-verified-content">
          <div className="email-verified-icon-container">
            <img 
              src={emailVerifiedIcon} 
              alt="Email Verified" 
              className="email-verified-icon" 
            />
          </div>
          <h1 className="email-verified-title">Verification Error</h1>
          <p className="email-verified-message">
            We encountered an issue verifying your email address
          </p>
          <div className="redirect-info">
            <p className="error-message">
              {verificationError}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state with countdown
  return (
    <div className="email-verified-container">
      <div className="email-verified-content">
        <div className="email-verified-icon-container">
          <img 
            src={emailVerifiedIcon} 
            alt="Email Verified" 
            className="email-verified-icon" 
          />
        </div>

        <h1 className="email-verified-title">Verified</h1>
        
        <p className="email-verified-message">
          We have successfully verified your email address.
        </p>

        {!isRedirecting && countdown > 0 && (
          <div className="redirect-info">
            <p className="countdown-text">
              Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
        )}

        {isRedirecting && (
          <div className="redirect-info">
            <p className="redirecting-text">Redirecting...</p>
          </div>
        )}
      </div>
      
      {/* Invalid Link Modal */}
      {showInvalidLinkModal && (
        <Modal isOpen={showInvalidLinkModal} onClose={() => {}} title="Invalid Link" showCloseButton={false}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', color: '#ff6b6b', marginBottom: '20px' }}>
              ⚠️
            </div>
            <h2 style={{ color: '#333', marginBottom: '15px' }}>
              Invalid or Expired Link
            </h2>
            <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>
              The email verification link you clicked is either invalid or has expired.
              You will be redirected to the homepage to start a new quote.
            </p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <p style={{ margin: '0', color: '#495057' }}>
                Redirecting in <strong>{redirectCountdown}</strong> second{redirectCountdown !== 1 ? 's' : ''}...
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/travel-portal/customer-portal' + utmQsParams}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Go to Homepage Now
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EmailVerifiedSection;